// Firestore `users/{uid}` shape aligns with id-tap-that-mobile UserMapper + brewer portal fields.
// All client writes that create or patch profile data use merge unless a full replace is intentional.

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  getAdditionalUserInfo,
} from 'firebase/auth';

const USERS_COLLECTION = 'users';

/**
 * Match legacy `users.lastSignIn` string shape (e.g. "2025-06-02 11:35:29.481442").
 * @param {Date} [date]
 * @returns {string}
 */
function formatLastSignInForUserDoc(date = new Date()) {
  const pad = (value, length = 2) => String(value).padStart(length, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const fractional = `${pad(date.getMilliseconds(), 3)}000`;
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${fractional}`;
}

/**
 * Derive display name from first + last the same way as mobile email registration.
 * @param {string} firstNameRaw
 * @param {string} lastNameRaw
 * @returns {string}
 */
function buildDisplayNameFromFirstLast(firstNameRaw, lastNameRaw) {
  return `${firstNameRaw} ${lastNameRaw}`;
}

/**
 * Stored `provider` field: mobile uses `email` for email/password (not `password`).
 * @param {string | null | undefined} providerId
 * @returns {string}
 */
function mapAuthProviderIdToStoredProvider(providerId) {
  if (!providerId) {
    return 'email';
  }
  if (providerId === 'google.com') {
    return 'google';
  }
  if (providerId === 'apple.com') {
    return 'apple';
  }
  if (
    providerId === 'password'
    || providerId === 'email'
    || providerId === 'password/email'
  ) {
    return 'email';
  }
  return 'email';
}

/**
 * @param {import('firebase/auth').User} firebaseUser
 * @returns {{ email: string, displayName: string, firstName: string, lastName: string }}
 */
function namesFromGoogleAdditionalProfile(firebaseUser, additionalInfo) {
  const profile = additionalInfo?.profile || {};
  const givenName = typeof profile.given_name === 'string' ? profile.given_name : null;
  const familyName = typeof profile.family_name === 'string' ? profile.family_name : null;

  let displayName =
    firebaseUser.displayName
    || (typeof profile.name === 'string' ? profile.name : null)
    || (typeof profile.displayName === 'string' ? profile.displayName : null)
    || '';

  let firstName = givenName || '';
  let lastName = familyName || '';

  if (givenName && familyName) {
    displayName = `${givenName} ${familyName}`;
  } else if ((!firstName || !lastName) && displayName && displayName.includes(' ')) {
    const spaceIndex = displayName.indexOf(' ');
    firstName = displayName.slice(0, spaceIndex);
    lastName = displayName.slice(spaceIndex + 1).trim();
  } else if (!firstName && displayName && !displayName.includes(' ')) {
    firstName = displayName;
    lastName = '';
  }

  const resolvedEmail =
    firebaseUser.email
    || firebaseUser.providerData?.find((entry) => entry.email)?.email
    || (typeof profile.email === 'string' ? profile.email : '')
    || '';

  return {
    email: resolvedEmail,
    displayName: displayName || '',
    firstName,
    lastName,
  };
}

/**
 * Minimal bootstrap when Auth has signed in but Firestore has no `users` doc yet.
 * @param {import('firebase/auth').User} firebaseUser
 * @param {string} lastSignIn
 * @returns {Record<string, unknown>}
 */
function buildBootstrapPayloadFromAuthUser(firebaseUser, lastSignIn) {
  const primaryProviderId = firebaseUser.providerData?.[0]?.providerId;
  const resolvedEmail =
    firebaseUser.email
    || firebaseUser.providerData?.find((entry) => entry.email)?.email
    || '';

  const displayNameRaw = firebaseUser.displayName?.trim() || '';
  let firstName = '';
  let lastName = '';
  const displayName = displayNameRaw;

  if (displayNameRaw.includes(' ')) {
    const spaceIndex = displayNameRaw.indexOf(' ');
    firstName = displayNameRaw.slice(0, spaceIndex);
    lastName = displayNameRaw.slice(spaceIndex + 1).trim();
  } else if (displayNameRaw) {
    firstName = displayNameRaw;
    lastName = '';
  }

  const hasDisplay = Boolean(displayName || (firstName && lastName));
  const hasEmail = Boolean(resolvedEmail);

  const nowIso = new Date().toISOString();

  return {
    id: firebaseUser.uid,
    email: resolvedEmail || null,
    displayName: displayName || (firstName && lastName ? buildDisplayNameFromFirstLast(firstName, lastName) : null),
    firstName: firstName || null,
    lastName: lastName || null,
    photoUrl: firebaseUser.photoURL || null,
    provider: mapAuthProviderIdToStoredProvider(primaryProviderId),
    needsProfileCompletion: !(hasDisplay && hasEmail),
    createdAt: nowIso,
    updatedAt: nowIso,
    lastSignIn,
  };
}

/**
 * After any successful sign-in: ensure `users/{uid}` exists, then refresh `lastSignIn`.
 * @param {import('firebase/firestore').Firestore} database
 * @param {import('firebase/auth').User} firebaseUser
 * @returns {Promise<void>}
 */
export async function syncUserDocumentAfterSignIn(database, firebaseUser) {
  if (!firebaseUser?.uid) {
    return;
  }
  const userRef = doc(database, USERS_COLLECTION, firebaseUser.uid);
  const snap = await getDoc(userRef);
  const lastSignIn = formatLastSignInForUserDoc();

  if (snap.exists()) {
    await updateDoc(userRef, {
      lastSignIn,
      updatedAt: new Date().toISOString(),
    });
    return;
  }

  await setDoc(
    userRef,
    buildBootstrapPayloadFromAuthUser(firebaseUser, lastSignIn),
    { merge: true },
  );
}

/**
 * Merge Google sign-in profile into `users/{uid}` (create or patch missing fields).
 * @param {import('firebase/firestore').Firestore} database
 * @param {import('firebase/auth').UserCredential} credential
 * @returns {Promise<void>}
 */
export async function upsertGoogleUserDocument(database, credential) {
  const firebaseUser = credential.user;
  if (!firebaseUser?.uid) {
    return;
  }

  const additionalInfo = getAdditionalUserInfo(credential);
  const { email: resolvedEmail, displayName, firstName, lastName } =
    namesFromGoogleAdditionalProfile(firebaseUser, additionalInfo);

  const userRef = doc(database, USERS_COLLECTION, firebaseUser.uid);
  const snap = await getDoc(userRef);
  const lastSignIn = formatLastSignInForUserDoc();

  const hasProfileData =
    Boolean(displayName && displayName.length > 0)
    && Boolean(resolvedEmail && resolvedEmail.length > 0);

  const nowIso = new Date().toISOString();

  if (!snap.exists()) {
    await setDoc(
      userRef,
      {
        id: firebaseUser.uid,
        email: resolvedEmail || null,
        displayName: displayName || null,
        firstName: firstName || null,
        lastName: lastName || null,
        photoUrl: firebaseUser.photoURL || null,
        provider: 'google',
        needsProfileCompletion: !hasProfileData,
        createdAt: nowIso,
        updatedAt: nowIso,
        lastSignIn,
      },
      { merge: true },
    );
    return;
  }

  const existing = snap.data() || {};
  const patch = {
    lastSignIn,
    updatedAt: nowIso,
  };

  if (resolvedEmail && !existing.email) {
    patch.email = resolvedEmail;
  }
  if (displayName && !existing.displayName) {
    patch.displayName = displayName;
  }
  if (firstName && !existing.firstName) {
    patch.firstName = firstName;
  }
  if (lastName !== undefined && lastName !== null && !existing.lastName) {
    patch.lastName = lastName;
  }
  if (!existing.photoUrl && firebaseUser.photoURL) {
    patch.photoUrl = firebaseUser.photoURL;
  }
  if (existing.needsProfileCompletion === undefined || existing.needsProfileCompletion === true) {
    patch.needsProfileCompletion = !hasProfileData;
  }

  await updateDoc(userRef, patch);
}

/**
 * Payload for brewer email/password registration (after Firebase Auth user exists).
 * @param {object} params
 * @param {string} params.uid
 * @param {string} params.emailNormalized
 * @param {string} params.firstName
 * @param {string} params.lastName
 * @param {string} params.breweryRole
 * @returns {Record<string, unknown>}
 */
export function buildEmailPasswordRegistrationDocument({
  uid,
  emailNormalized,
  firstName,
  lastName,
  breweryRole,
}) {
  const displayName = buildDisplayNameFromFirstLast(firstName, lastName);
  const nowIso = new Date().toISOString();
  const lastSignIn = formatLastSignInForUserDoc();

  const hasDisplay = Boolean(displayName && displayName.length > 0);
  const hasEmail = Boolean(emailNormalized && emailNormalized.length > 0);

  return {
    id: uid,
    email: emailNormalized,
    displayName,
    firstName,
    lastName,
    photoUrl: null,
    provider: 'email',
    needsProfileCompletion: !(hasDisplay && hasEmail),
    createdAt: nowIso,
    updatedAt: nowIso,
    breweryRole,
    lastSignIn,
  };
}

export { USERS_COLLECTION };
